
import * as THREE from "../../libs/three.js/build/three.module.js";
import {FeatureZone} from "./FeatureZone.js";
import {EventDispatcher} from "../EventDispatcher.js";

export class FeatureZoneTool extends EventDispatcher {

	constructor(viewer) {
		super();

		this.viewer = viewer;
		this.highlightOverlay = null;

		this.scene = new THREE.Scene();
		this.scene.name = "scene_feature_zones";

		this.viewer.inputHandler.registerInteractiveScene(this.scene);

		this.onAdd = (e) => {
			let zone = e.featureZone;
			this.scene.add(zone);

			zone.addEventListener("select", (event) => {
				if (event.domEvent && (event.domEvent.ctrlKey || event.domEvent.metaKey)) {
					this.viewer.landmarkDistanceTool.toggleInGroup(zone);
				} else {
					this.onFeatureZoneClicked(zone);
				}
			});
		};

		this.onRemove = (e) => {
			this.scene.remove(e.featureZone);
		};

		// Wire up existing zones
		for (let zone of viewer.scene.featureZones) {
			this.onAdd({ featureZone: zone });
		}

		viewer.addEventListener("scene_changed", this.onSceneChange.bind(this));
		viewer.addEventListener("render.pass.scene", (e) => this.render(e));

		viewer.scene.addEventListener("feature_zone_added", this.onAdd);
		viewer.scene.addEventListener("feature_zone_removed", this.onRemove);
	}

	onSceneChange(e) {
		if (e.oldScene) {
			e.oldScene.removeEventListeners("feature_zone_added", this.onAdd);
			e.oldScene.removeEventListeners("feature_zone_removed", this.onRemove);
		}

		e.scene.addEventListener("feature_zone_added", this.onAdd);
		e.scene.addEventListener("feature_zone_removed", this.onRemove);
	}

	onFeatureZoneClicked(featureZone) {
		// Remove existing overlay
		if (this.highlightOverlay) {
			this.viewer.scene.scene.remove(this.highlightOverlay);
			this.highlightOverlay = null;
		}

		let matchingPositions = [];
		let pointclouds = this.viewer.scene.pointclouds;

		for (let pointcloud of pointclouds) {
			if (!pointcloud.visible) {
				continue;
			}

			let nodes = pointcloud.visibleNodes;
			if (!nodes) {
				continue;
			}

			for (let node of nodes) {
				let geometry = node.geometryNode.geometry;
				if (!geometry) {
					continue;
				}

				let positionAttribute = geometry.attributes.position;
				if (!positionAttribute) {
					continue;
				}

				let nodeWorld = node.sceneNode.matrixWorld;
				let positions = positionAttribute.array;
				let numPoints = positionAttribute.count;

				for (let i = 0; i < numPoints; i++) {
					let lx = positions[i * 3 + 0];
					let ly = positions[i * 3 + 1];
					let lz = positions[i * 3 + 2];

					// Transform to world coords
					let wx = nodeWorld.elements[0] * lx + nodeWorld.elements[4] * ly + nodeWorld.elements[8] * lz + nodeWorld.elements[12];
					let wy = nodeWorld.elements[1] * lx + nodeWorld.elements[5] * ly + nodeWorld.elements[9] * lz + nodeWorld.elements[13];
					let wz = nodeWorld.elements[2] * lx + nodeWorld.elements[6] * ly + nodeWorld.elements[10] * lz + nodeWorld.elements[14];

					if (featureZone.containsPoint(wx, wy, wz)) {
						matchingPositions.push(wx, wy, wz);
					}
				}
			}
		}

		if (matchingPositions.length > 0) {
			// Use the feature zone's position as the origin for the overlay
			// to avoid float32 precision jitter with large projected coordinates.
			let origin = featureZone.position;

			let localPositions = new Float32Array(matchingPositions.length);
			for (let i = 0; i < matchingPositions.length; i += 3) {
				localPositions[i + 0] = matchingPositions[i + 0] - origin.x;
				localPositions[i + 1] = matchingPositions[i + 1] - origin.y;
				localPositions[i + 2] = matchingPositions[i + 2] - origin.z;
			}

			let bufferGeometry = new THREE.BufferGeometry();
			bufferGeometry.setAttribute(
				"position",
				new THREE.Float32BufferAttribute(localPositions, 3)
			);

			let pointsMaterial = new THREE.PointsMaterial({
				color: 0xff0000,
				size: 3,
				sizeAttenuation: false,
			});

			this.highlightOverlay = new THREE.Points(bufferGeometry, pointsMaterial);
			this.highlightOverlay.position.copy(origin);
			this.viewer.scene.scene.add(this.highlightOverlay);
		}

		let pointCount = matchingPositions.length / 3;
		this.dispatchEvent({
			type: "feature_zone_selected",
			featureZone: featureZone,
			pointCount: pointCount,
		});
	}

	render(params) {
		const renderer = this.viewer.renderer;
		const oldTarget = renderer.getRenderTarget();

		if (params.renderTarget) {
			renderer.setRenderTarget(params.renderTarget);
		}
		renderer.render(this.scene, this.viewer.scene.getActiveCamera());
		renderer.setRenderTarget(oldTarget);
	}
}
